import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FileText } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import Chip from '../../../components/Chip';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import EmptyState from '../../../components/EmptyState';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
} from '../../../components/StandardTable';
import { DeleteIcon, OpenExternalIcon, UploadIcon } from '../../../components/icons';
import ActionsHeaderHelp from '../../../components/ActionsHeaderHelp';
import AdminModal from '../../components/AdminModal';
import { formatDate } from '../../lib/format';
import { apiError, inputClasses, invalidateInitiativeQueries, labelClasses } from './InitiativeDetailPage';

export interface InitiativeDocument {
  id: string;
  kind: 'traceability_record' | 'other';
  label: string;
  version_label: string | null;
  original_name: string;
  mime_type: string;
  file_size: number | string;
  sha256: string;
  notes: string | null;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  uploaded_at: string;
}

interface Props {
  initiativeId: string;
}

const KIND_LABELS: Record<InitiativeDocument['kind'], string> = {
  traceability_record: 'Traceability record',
  other: 'Other',
};

/** Mirrors the server's multer rules (PRD v2 §5.13.5); the server stays the authority. */
export const MAX_DOCUMENT_BYTES = 15 * 1024 * 1024;
export const ACCEPTED_DOCUMENT_TYPES: Record<string, string> = {
  'application/pdf': '.pdf',
  'text/markdown': '.md',
  'text/plain': '.txt',
  'application/json': '.json',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
};
const ACCEPT_ATTR = [...Object.values(ACCEPTED_DOCUMENT_TYPES), ...Object.keys(ACCEPTED_DOCUMENT_TYPES)].join(',');

/**
 * "v1.0" from `amendment-81-v1.0-record-20260912-1128.pdf` — the explainer skill's
 * record filenames carry the version between hyphens. Anything else pre-fills nothing.
 */
export const versionLabelFromFilename = (filename: string): string => {
  const match = /-v(\d+\.\d+)-/.exec(filename);
  return match ? `v${match[1]}` : '';
};

/**
 * Client-side check so an obvious mistake never leaves the browser. Browsers report
 * `.md` with an empty or vendor type on some platforms, so the extension is checked
 * when the type is missing.
 */
export const documentFileProblem = (file: { name: string; type: string; size: number }): string | null => {
  const ext = file.name.includes('.') ? `.${file.name.split('.').pop()!.toLowerCase()}` : '';
  const typeAccepted = file.type in ACCEPTED_DOCUMENT_TYPES;
  const extAccepted = Object.values(ACCEPTED_DOCUMENT_TYPES).includes(ext);
  if (!typeAccepted && !(file.type === '' && extAccepted)) {
    return 'Invalid file type. Accepted: PDF, Markdown, plain text, JSON, or Word (.docx).';
  }
  if (file.size > MAX_DOCUMENT_BYTES) {
    return 'File too large. Maximum size is 15 MB.';
  }
  return null;
};

export const formatFileSize = (bytes: number | string): string => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const EMPTY_FORM = { kind: 'traceability_record' as InitiativeDocument['kind'], label: '', version_label: '', notes: '' };

/**
 * Supporting documents on a measure — admin only (PRD v2 §5.13).
 *
 * The explainer workflow produces a traceability record per version; this card is
 * where each one is kept against the measure it documents. It sits last on the
 * Citizen Content tab because the record is *about* the fields on this tab.
 * Nothing here reaches Pro, citizen, or public surfaces: the list comes from its own
 * admin route, and downloads are short-lived signed URLs.
 */
const InitiativeDocumentsCard: React.FC<Props> = ({ initiativeId }) => {
  const queryClient = useQueryClient();
  const queryKey = ['admin-elections', 'initiative', initiativeId, 'documents'];
  const [uploadModal, setUploadModal] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<InitiativeDocument | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  const documents = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get(`/admin/ballot-initiatives/${initiativeId}/documents`);
      return (res.data?.data ?? []) as InitiativeDocument[];
    },
  });
  const rows = useMemo(() => documents.data ?? [], [documents.data]);

  // Same bytes uploaded twice is allowed; the newer row says so (AC 46). Rows are
  // newest first, so "earlier" is any row after this one with the same hash.
  const duplicateOf = useMemo(() => {
    const map = new Map<string, InitiativeDocument>();
    for (let i = 0; i < rows.length; i += 1) {
      const earlier = rows.slice(i + 1).find((other) => other.sha256 === rows[i].sha256);
      if (earlier) map.set(rows[i].id, earlier);
    }
    return map;
  }, [rows]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey });
    invalidateInitiativeQueries(queryClient, initiativeId);
  };

  const openUpload = () => {
    setFile(null);
    setForm(EMPTY_FORM);
    setError(null);
    setFileInputKey((k) => k + 1);
    setUploadModal(true);
  };

  const pickFile = (picked: File | null) => {
    setFile(picked);
    if (!picked) return;
    setError(documentFileProblem(picked));
    setForm((current) => ({
      ...current,
      label: current.label.trim() ? current.label : picked.name,
      version_label: current.version_label.trim() ? current.version_label : versionLabelFromFilename(picked.name),
    }));
  };

  const upload = useMutation({
    mutationFn: () => {
      const formData = new FormData();
      formData.append('file', file as File);
      formData.append('kind', form.kind);
      formData.append('label', form.label.trim());
      formData.append('version_label', form.version_label.trim());
      formData.append('notes', form.notes.trim());
      // The shared client drops the JSON content-type for FormData bodies.
      return api.post(`/admin/ballot-initiatives/${initiativeId}/documents`, formData);
    },
    onSuccess: () => {
      toast.success('Document uploaded');
      setUploadModal(false);
      refresh();
    },
    onError: (err: any) => setError(apiError(err, 'Failed to upload the document')),
  });

  const remove = useMutation({
    mutationFn: (doc: InitiativeDocument) =>
      api.delete(`/admin/ballot-initiatives/${initiativeId}/documents/${doc.id}`),
    onSuccess: () => {
      toast.success('Document removed');
      setConfirmDelete(null);
      refresh();
    },
    onError: (err: any) => toast.error(apiError(err, 'Failed to remove the document')),
  });

  // A plain link cannot carry the Bearer header, so ask for the signed URL and open
  // it from here. Same approach as the data-management file download.
  const download = useMutation({
    mutationFn: async (doc: InitiativeDocument) => {
      const res = await api.get(`/admin/ballot-initiatives/${initiativeId}/documents/${doc.id}/download?format=json`);
      return { url: res.data?.data?.download_url as string, filename: doc.original_name };
    },
    onSuccess: ({ url, filename }) => {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onError: (err: any) => toast.error(apiError(err, 'Failed to prepare the download')),
  });

  const copyFingerprint = async (sha256: string) => {
    try {
      await navigator.clipboard.writeText(sha256);
      toast.success('Fingerprint copied');
    } catch {
      toast.error('Could not copy the fingerprint');
    }
  };

  const canSubmit = Boolean(file) && !error && form.label.trim().length > 0 && !upload.isPending;

  return (
    <div className="card p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">
          Supporting Documents ({rows.length})
        </h3>
        <Button variant="primary" size="sm" icon={UploadIcon} onClick={openUpload}>Upload</Button>
      </div>

      <p className="mb-4 text-xs text-text-tertiary dark:text-text-dark-tertiary">
        Staff-only files kept against this measure — the traceability record for each explainer
        version. Never shown to Pro, citizen, or public readers.
      </p>

      {documents.isError ? (
        <EmptyState size="compact" tone="error" title="Could not load documents" hint={apiError(documents.error, 'Try again in a moment.')} />
      ) : rows.length === 0 && !documents.isLoading ? (
        <EmptyState
          size="compact"
          icon={FileText}
          title="No supporting documents yet."
          hint="Upload the traceability record for each explainer version."
        />
      ) : (
        <StandardTable loading={documents.isLoading} horizontalScroll>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell>Label</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit">Kind</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="nowrap">Uploaded</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit" align="right">Size</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="nowrap">Fingerprint</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="actions" align="right">
                <span className="inline-flex items-center gap-1.5">
                  Actions
                  <ActionsHeaderHelp
                    icons={[
                      { key: 'open-external', note: 'Opens the stored file through a 15-minute signed link.' },
                      { key: 'delete', note: 'Removes the stored file as well as this row.' },
                    ]}
                  />
                </span>
              </StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {rows.map((doc) => {
              const sameAs = duplicateOf.get(doc.id);
              return (
                <StandardTableRow key={doc.id}>
                  <StandardTableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium text-text-primary dark:text-text-dark-primary">{doc.label}</span>
                      {doc.version_label && <Chip label={doc.version_label} tone="info" size="sm" />}
                    </div>
                    {doc.label !== doc.original_name && (
                      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{doc.original_name}</p>
                    )}
                    {doc.notes && (
                      <p className="mt-0.5 text-xs text-text-secondary dark:text-text-dark-secondary">{doc.notes}</p>
                    )}
                    {sameAs && (
                      <p className="mt-0.5 text-xs italic text-text-tertiary dark:text-text-dark-tertiary">
                        Same content as {sameAs.label}
                      </p>
                    )}
                  </StandardTableCell>
                  <StandardTableCell>{KIND_LABELS[doc.kind] ?? doc.kind}</StandardTableCell>
                  <StandardTableCell>
                    <div>{formatDate(doc.uploaded_at)}</div>
                    {doc.uploaded_by_name && (
                      <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">by {doc.uploaded_by_name}</div>
                    )}
                  </StandardTableCell>
                  <StandardTableCell align="right">{formatFileSize(doc.file_size)}</StandardTableCell>
                  <StandardTableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="font-mono text-xs"
                      title={doc.sha256}
                      ariaLabel={`Copy fingerprint ${doc.sha256}`}
                      onClick={() => copyFingerprint(doc.sha256)}
                    >
                      {doc.sha256.slice(0, 16)}
                    </Button>
                  </StandardTableCell>
                  <StandardTableCell align="right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={OpenExternalIcon}
                        ariaLabel={`Download ${doc.label}`}
                        loading={download.isPending && download.variables?.id === doc.id}
                        onClick={() => download.mutate(doc)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={DeleteIcon}
                        ariaLabel={`Remove ${doc.label}`}
                        onClick={() => setConfirmDelete(doc)}
                      />
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              );
            })}
          </StandardTableBody>
        </StandardTable>
      )}

      <AdminModal
        isOpen={uploadModal}
        onClose={() => setUploadModal(false)}
        title="Upload Supporting Document"
        size="lg"
        footer={<>
          <Button variant="secondary" size="sm" onClick={() => setUploadModal(false)}>Cancel</Button>
          <Button variant="primary" size="sm" loading={upload.isPending} disabled={!canSubmit} onClick={() => upload.mutate()}>
            Upload
          </Button>
        </>}
      >
        <div className="space-y-4">
          {error && (
            <p className="rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">{error}</p>
          )}
          <div>
            <label htmlFor="doc-file" className={labelClasses}>File *</label>
            <input
              key={fileInputKey}
              id="doc-file"
              type="file"
              accept={ACCEPT_ATTR}
              className="input file:mr-3 file:rounded file:border-0 file:bg-background-tertiary file:px-2 file:py-1 file:text-xs file:text-text-primary dark:file:bg-background-dark-quaternary dark:file:text-text-dark-primary"
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
            />
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              PDF, Markdown, plain text, JSON, or Word (.docx), up to 15 MB.
            </p>
          </div>
          <div>
            <label htmlFor="doc-kind" className={labelClasses}>Kind</label>
            <select
              id="doc-kind"
              value={form.kind}
              onChange={(e) => setForm((c) => ({ ...c, kind: e.target.value as InitiativeDocument['kind'] }))}
              className={inputClasses}
            >
              {Object.entries(KIND_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="doc-label" className={labelClasses}>Label *</label>
            <input
              id="doc-label"
              type="text"
              value={form.label}
              maxLength={255}
              onChange={(e) => setForm((c) => ({ ...c, label: e.target.value }))}
              className={inputClasses}
              placeholder="Pre-filled from the filename"
            />
          </div>
          <div>
            <label htmlFor="doc-version" className={labelClasses}>Version</label>
            <input
              id="doc-version"
              type="text"
              value={form.version_label}
              maxLength={32}
              onChange={(e) => setForm((c) => ({ ...c, version_label: e.target.value }))}
              className={inputClasses}
              placeholder="e.g. v1.0"
            />
          </div>
          <div>
            <label htmlFor="doc-notes" className={labelClasses}>Notes</label>
            <textarea
              id="doc-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm((c) => ({ ...c, notes: e.target.value }))}
              className={inputClasses}
              placeholder="Optional — e.g. review status, what changed in this version"
            />
          </div>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={confirmDelete !== null}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete)}
        title="Remove this document?"
        message={confirmDelete ? `"${confirmDelete.label}" and its stored file will be removed. This cannot be undone.` : ''}
        confirmText="Remove"
        variant="danger"
      />
    </div>
  );
};

export default InitiativeDocumentsCard;
